import {
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { TenantApiError } from "@qr-menu/api-tenant";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";
import {
  MAX_PASSWORD_BYTES,
  isBlank,
  runeLength,
  utf8ByteLength,
} from "../lib/passwordValidation";

const REQUIRED_FIELDS_MESSAGE = "Yeni parola ve tekrarı zorunludur.";
const MISMATCH_MESSAGE = "Yeni parolalar eşleşmiyor.";
const TOO_SHORT_MESSAGE = "Yeni parola en az 8 karakter olmalıdır.";
const TOO_LONG_MESSAGE = "Yeni parola çok uzun.";
const GENERIC_FAILURE_MESSAGE =
  "Şu anda işlem gerçekleştirilemiyor. Lütfen daha sonra tekrar deneyin.";
const RESET_SUCCESS_MESSAGE =
  "Parolanız güncellendi. Yeni parolanızla giriş yapabilirsiniz.";

// A loose sanity check only — length and URL-safe base64 charset. The
// backend (AuthService.ResetPassword / parseCanonicalResetToken) remains the
// sole authority on the exact canonical format (43 chars, strict Raw URL
// base64, 32 decoded bytes, canonical re-encoding); this is not a second
// copy of that policy, just enough to reject an obviously-wrong fragment
// (empty, or carrying something that isn't a token at all) before ever
// showing a form for it.
const TOKEN_CHARSET_PATTERN = /^[A-Za-z0-9_-]+$/;
const MIN_PLAUSIBLE_TOKEN_LENGTH = 20;

function isPlausibleToken(value: string): boolean {
  return (
    value.length >= MIN_PLAUSIBLE_TOKEN_LENGTH &&
    TOKEN_CHARSET_PATTERN.test(value)
  );
}

// Reads only the URL fragment (window.location.hash), never the query
// string: the reset link is <origin>/reset-password#token=<token>, and the
// token must never appear in a query parameter, which browsers and proxies
// commonly log. Manual parsing on purpose — no URLSearchParams/useSearchParams
// on location.search, since that would read the wrong part of the URL.
function extractTokenFromFragment(hash: string): string | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;

  for (const pair of raw.split("&")) {
    const [key, value] = pair.split("=");
    if (key === "token" && value) {
      try {
        return decodeURIComponent(value);
      } catch {
        return null;
      }
    }
  }
  return null;
}

export function ResetPasswordRoute() {
  // Read once, synchronously, during the first render — directly from
  // window.location.hash. This is the ONLY place the token is ever read
  // from the URL. From here on it lives only in this component's state:
  // never in router/navigation state, a global store, localStorage,
  // sessionStorage, a cookie, the tenant session module, a log line, or an
  // error message.
  const [token] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const extracted = extractTokenFromFragment(window.location.hash);
    return extracted && isPlausibleToken(extracted) ? extracted : null;
  });

  // Strips the fragment from the visible address bar before the browser
  // paints, so the raw token spends as little time as possible visible in
  // the URL bar, browser history, or anywhere a screenshot/share could catch
  // it. A refresh after this point has no fragment to read — that loss is
  // intentional; see the no-token state below. Guarded to run only once:
  // React 18 StrictMode double-invokes effects in development, and a second
  // replaceState call would otherwise be a harmless but pointless no-op.
  //
  // Passes the CURRENT history.state through rather than null: react-router's
  // BrowserRouter (createBrowserHistory in @remix-run/router) already calls
  // history.replaceState itself while mounting, before this component's
  // effects run, to stamp window.history.state with its own { idx, usr, key }
  // bookkeeping. Passing null here would silently overwrite that object,
  // corrupting react-router's internal index tracking (used for back/forward
  // delta and scroll restoration) for this history entry. Only the URL
  // changes; the state object is carried through unmodified.
  const strippedRef = useRef(false);
  useLayoutEffect(() => {
    if (strippedRef.current) return;
    strippedRef.current = true;
    if (typeof window === "undefined") return;
    if (window.location.hash) {
      const url = window.location.pathname + window.location.search;
      window.history.replaceState(window.history.state, "", url);
    }
  }, []);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // A rejected token (422: unknown/expired/already used/inactive user — the
  // backend deliberately never distinguishes which) ends the attempt: the
  // token itself is fixed for this mount, so resubmitting the same form
  // cannot succeed. This swaps the form out for the same actionable,
  // link-to-/forgot-password state used when there was no token at all.
  const [tokenRejected, setTokenRejected] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !token) return;

    if (!newPassword || !confirmPassword) {
      setError(REQUIRED_FIELDS_MESSAGE);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(MISMATCH_MESSAGE);
      return;
    }
    if (isBlank(newPassword)) {
      setError(REQUIRED_FIELDS_MESSAGE);
      return;
    }
    if (runeLength(newPassword) < 8) {
      setError(TOO_SHORT_MESSAGE);
      return;
    }
    if (utf8ByteLength(newPassword) > MAX_PASSWORD_BYTES) {
      setError(TOO_LONG_MESSAGE);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await tenantApi.resetPassword({ token, new_password: newPassword });
      setNewPassword("");
      setConfirmPassword("");
      // No auto-login and nothing written to the tenant session store: the
      // caller signs in fresh with the new password, same as every other
      // credential-affecting flow in this panel (see Account.tsx).
      navigate("/login", {
        replace: true,
        state: { message: RESET_SUCCESS_MESSAGE },
      });
    } catch (err) {
      // Status-only mapping, never the backend's error_message text: with the
      // client-side checks above already covering every new_password rule
      // the backend enforces (required/notblank/min 8 runes/max 72 UTF-8
      // bytes) and the token itself already sanity-checked before the form
      // even renders, a 422 reaching here can only mean the token was
      // rejected (unknown/expired/used/inactive user) — the same convention
      // Account.tsx uses for change-password's current-password 422.
      if (err instanceof TenantApiError && err.status === 422) {
        setTokenRejected(true);
        return;
      }
      setError(GENERIC_FAILURE_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="auth-shell">
        <Card className="auth-card">
          <CardHeader>
            <CardTitle>Parola sıfırlama</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="state error">
              Bu parola sıfırlama bağlantısı bu oturumda kullanılamıyor.
              <br />
              E-postadaki bağlantıyı tekrar açın veya yeni bir parola
              sıfırlama isteği gönderin.
            </div>
            <p className="hint">
              <Link to="/forgot-password">Yeni bağlantı iste</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenRejected) {
    return (
      <div className="auth-shell">
        <Card className="auth-card">
          <CardHeader>
            <CardTitle>Parola sıfırlama</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="state error">
              Bu parola sıfırlama bağlantısı geçersiz veya süresi dolmuş.
              <br />
              Lütfen yeni bir parola sıfırlama isteği oluşturun.
            </div>
            <p className="hint">
              <Link to="/forgot-password">Yeni bağlantı iste</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <CardHeader>
          <CardTitle>Yeni parola belirle</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="login-form" onSubmit={handleSubmit}>
            <Field label="Yeni parola" htmlFor="reset-password">
              <input
                id="reset-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                spellCheck={false}
                required
              />
            </Field>
            <Field label="Yeni parolayı tekrar" htmlFor="reset-password-confirm">
              <input
                id="reset-password-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                spellCheck={false}
                required
              />
            </Field>
            {error && <div className="state error">{error}</div>}
            <div className="login-actions">
              <button type="submit" className="primary" disabled={submitting}>
                {submitting ? "Kaydediliyor…" : "Parolayı güncelle"}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
